import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { useServerTable } from '@/hooks/use-server-table';
import {
  useDepartmentOptions,
  useDesignationOptions,
  useEmployeesByUserIds,
} from '@/modules/shared/hr-lookups';
import {
  DEFAULT_PM_REVIEWS_ORDER,
  EMPTY_REVIEW_FILTERS,
  ReviewFilters,
  ReviewFilterValue,
  useCurrentUserId,
} from '@/modules/performance/hr-review-core';
import { createSubmissionsTableColumns } from '../../components/submissions-table/submissions-table-columns';
import { useGetSubmissions } from '../../hooks/use-submissions';
import { PageHeader } from '@/components/core';

export const SubmissionsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_PM_REVIEWS_ORDER,
  });
  const [filters, setFilters] = useState<ReviewFilterValue>(EMPTY_REVIEW_FILTERS);

  const { data: departments = [] } = useDepartmentOptions();
  const { data: designations = [] } = useDesignationOptions();
  const departmentNames = useMemo(
    () => new Map(departments.map((option) => [option.ItemId, option.Title])),
    [departments]
  );
  const designationNames = useMemo(
    () => new Map(designations.map((option) => [option.ItemId, option.Title])),
    [designations]
  );

  const { data, isLoading, error } = useGetSubmissions(userId, filters, {
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    order,
    columnWhere,
  });
  const { byUserId: employeesByUserId } = useEmployeesByUserIds(
    (data?.items ?? []).map((row) => row.review?.ReviewForId ?? '')
  );
  const emailOf = (userId: string) => employeesByUserId.get(userId)?.Email;

  const handleFiltersChange = (next: ReviewFilterValue) => {
    setFilters(next);
    resetPage();
  };

  const openSubmission = (itemId: string) => navigate(`/hr/my-submissions/${itemId}`);
  const columns = createSubmissionsTableColumns({
    t,
    departmentNames,
    designationNames,
    emailOf,
    onOpen: (row) => openSubmission(row.detail.ItemId),
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('MY_REVIEW_SUBMISSIONS')} />

      <ReviewFilters value={filters} onChange={handleFiltersChange} lockSingleDepartment={false} />

      <AdvanceDataTable
        {...tableProps}
        data={data?.items ?? []}
        columns={columns}
        onRowClick={(row) => openSubmission(row.detail.ItemId)}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: data?.totalCount ?? 0,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />
    </div>
  );
};
